import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import {
  buildEmptyLegalContentForm,
  buildSandboxPreviewHtml,
  normalizeLegalHtmlForSave,
} from '../model/system-legal.model.js';

function LegalContentEditorTab({
  tabKey,
  label,
  form,
  errors,
  setContentField,
  saveContentPage,
  refreshContentPage,
  isLoading,
  isSaving,
}) {
  const htmlTextareaRef = useRef(null);
  const quillRef = useRef(null);
  const [htmlMode, setHtmlMode] = useState('editor');
  const contentForm = form || buildEmptyLegalContentForm(tabKey);
  const previewHtml = buildSandboxPreviewHtml(contentForm.html);

  useEffect(() => {
    setHtmlMode('editor');
  }, [tabKey]);

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block', 'link'],
      ['clean'],
    ],
  };

  return (
    <LoadingContainer isLoading={isLoading} hasData className="">
      <div className="row g-3">
        <div className="col-xl-7">
          <div className="card card-body h-100">
            <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
              <h5 className="mb-0">{label}</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => refreshContentPage(tabKey)}
                disabled={isSaving || isLoading}
              >
                <i className="ti ti-refresh me-1" aria-hidden="true" />
                Refresh
              </button>
            </div>

            <div className="row g-3">
              <div className="col-md-7">
                <label className="form-label" htmlFor={`legal-content-title-${tabKey}`}>Title *</label>
                <input
                  id={`legal-content-title-${tabKey}`}
                  className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                  value={contentForm.title}
                  onChange={(event) => setContentField(tabKey, 'title', event.target.value)}
                  disabled={isSaving}
                />
                {errors.title ? <div className="invalid-feedback">{errors.title}</div> : null}
              </div>
              <div className="col-md-2">
                <label className="form-label" htmlFor={`legal-content-version-${tabKey}`}>Version *</label>
                <input
                  id={`legal-content-version-${tabKey}`}
                  className={`form-control ${errors.version ? 'is-invalid' : ''}`}
                  value={contentForm.version}
                  onChange={(event) => setContentField(tabKey, 'version', event.target.value)}
                  disabled={isSaving}
                />
                {errors.version ? <div className="invalid-feedback">{errors.version}</div> : null}
              </div>
              <div className="col-md-3">
                <label className="form-label" htmlFor={`legal-content-effective-date-${tabKey}`}>Effective date *</label>
                <input
                  id={`legal-content-effective-date-${tabKey}`}
                  type="date"
                  className={`form-control ${errors.effectiveDate ? 'is-invalid' : ''}`}
                  value={contentForm.effectiveDate}
                  onChange={(event) => setContentField(tabKey, 'effectiveDate', event.target.value)}
                  disabled={isSaving}
                />
                {errors.effectiveDate ? <div className="invalid-feedback">{errors.effectiveDate}</div> : null}
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label mb-0">HTML *</label>
                <div className="d-flex align-items-center gap-2">
                  <span className={`small fw-semibold ${htmlMode === 'editor' ? 'text-primary' : 'text-muted'}`}>Editor</span>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={htmlMode === 'html'}
                      onChange={(event) => {
                        const nextMode = event.target.checked ? 'html' : 'editor';
                        if (nextMode === 'html') {
                          const normalizedHtml = normalizeLegalHtmlForSave(contentForm.html);
                          if (normalizedHtml !== contentForm.html) {
                            setContentField(tabKey, 'html', normalizedHtml);
                          }
                        }
                        setHtmlMode(nextMode);
                      }}
                      disabled={isSaving}
                    />
                  </div>
                  <span className={`small fw-semibold ${htmlMode === 'html' ? 'text-primary' : 'text-muted'}`}>HTML</span>
                </div>
              </div>

              {htmlMode === 'editor' ? (
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  className="app-email-template-editor"
                  value={contentForm.html}
                  modules={quillModules}
                  onChange={(value) => setContentField(tabKey, 'html', value)}
                  readOnly={isSaving}
                />
              ) : (
                <textarea
                  ref={htmlTextareaRef}
                  rows="14"
                  className={`form-control font-monospace ${errors.html ? 'is-invalid' : ''}`}
                  value={contentForm.html}
                  onChange={(event) => setContentField(tabKey, 'html', event.target.value)}
                  disabled={isSaving}
                />
              )}
              {errors.html ? <div className="invalid-feedback d-block">{errors.html}</div> : null}
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => saveContentPage(tabKey)}
                disabled={isSaving}
              >
                <i className="ti ti-device-floppy me-2" aria-hidden="true" />
                Save
              </button>
            </div>
          </div>
        </div>

        <div className="col-xl-5">
          <div className="card card-body h-100">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
              <h5 className="mb-0">Preview</h5>
              <span className="text-muted small">{contentForm.version || '-'}</span>
            </div>
            <div className="border rounded p-3 bg-white" style={{ minHeight: '320px' }}>
              {contentForm.html ? (
                <iframe
                  title={`${label} preview`}
                  sandbox=""
                  srcDoc={previewHtml}
                  className="border-0 w-100"
                  style={{ minHeight: '280px' }}
                />
              ) : (
                <div className="text-muted">No content to preview.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LoadingContainer>
  );
}

export default LegalContentEditorTab;
